import { Module, Mutation, Action } from "vuex-class-modules"

import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { projectModule } from "./project.module"
import { ServerResponse } from "@/models/ServerResponse"
import { Comment, PayloadCommentCount, CommentContentType } from "@/models/Comment"
import {
  AlertContentItem,
  FilterComment,
  StructuredDescription,
  EmojiType,
  SubscriptionCommentDTO
} from "@/models"
import { store } from "../store"
import { Modules } from "../modules"
import { BaseModule } from "@/models/BaseModule"
import { AlertActions, alertModule } from "./alert.module"
import { AlertType } from "@/models"
import { $t } from "@/plugins/i18n"
import { accountModule } from "./account.module"
import { parsingServerResponseContent } from "@/utils/helpers"
import { v4 as uuidv4 } from "uuid"

export enum CommentActions {
  GET_COMMENTS = "GET_COMMENTS",
  GET_DEMO_VIDEO_COMMENTS = "GET_DEMO_VIDEO_COMMENTS",
  GET_COMMENT = "GET_COMMENT",
  GET_COMMENTS_BY_SOURCE = "GET_COMMENTS_BY_SOURCE",
  POST_COMMENT = "POST_COMMENT",
  UPDATE_COMMENT_DESCRIPTION = "UPDATE_COMMENT_DESCRIPTION",
  DELETE_COMMENT = "DELETE_COMMENT",
  UPDATE_COMMENT_TEXT_IN_LIST = "UPDATE_COMMENT_TEXT_IN_LIST",
  SET_FILES_TO_COMMENT = "SET_FILES_TO_COMMENT",
  SET_FILES_TO_DEMO_VIDEO_COMMENT = "SET_FILES_TO_DEMO_VIDEO_COMMENT",
  GET_COMMENT_COUNT = "GET_COMMENT_COUNT",
  UPDATE_COMMENT_RESTORE = "UPDATE_COMMENT_RESTORE",
  UPDATE_COMMENT_VALUE_IN_LIST = "UPDATE_COMMENT_VALUE_IN_LIST",
  UPDATE_DEMO_VIDEO_COMMENT_VALUE_IN_LIST = "UPDATE_DEMO_VIDEO_COMMENT_VALUE_IN_LIST",
  SET_COMMENT_REACTION = "SET_COMMENT_REACTION",
  SUBSCRIPTION_COMMENTS_CHANGE = "SUBSCRIPTION_COMMENTS_CHANGE",
  RESET_COMMENTS = "RESET_COMMENTS",
  NEW_COMMENTS_COUNT = "NEW_COMMENTS_COUNT",
  SET_COMMENT_IN_LIST = "SET_COMMENT_IN_LIST",
  SET_DEMO_VIDEO_COMMENT_IN_LIST = "SET_DEMO_VIDEO_COMMENT_IN_LIST",
  REMOVE_COMMENT_FROM_LIST = "REMOVE_COMMENT_FROM_LIST",
  REMOVE_DEMO_VIDEO_COMMENT_FROM_LIST = "REMOVE_DEMO_VIDEO_COMMENT_FROM_LIST",
  SET_COMMENT_RESPONSE = "SET_COMMENT_RESPONSE",
  SET_DEMO_VIDEO_COMMENT_RESPONSE = "SET_DEMO_VIDEO_COMMENT_RESPONSE",
  REPLACE_COMMENT_IN_LIST = "REPLACE_COMMENT_IN_LIST",
  REPLACE_DEMO_VIDEO_COMMENT_IN_LIST = "REPLACE_DEMO_VIDEO_COMMENT_IN_LIST",
  RESET_DEMO_VIDEO_COMMENTS = "RESET_DEMO_VIDEO_COMMENTS"
}

@Module
class CommentModule extends BaseModule {
  comments: ServerResponse<Comment> = new ServerResponse<Comment>()
  demoVideoComments: ServerResponse<Comment> = new ServerResponse<Comment>()
  commentsBySource: Comment[] = []
  comment: Comment
  commentCount = 0
  newCommentsCount = 0

  get projectId() {
    return projectModule.currentProjectId
  }

  get accountId() {
    return accountModule.accountId
  }

  get [`get/${CommentActions.GET_COMMENTS}`]() {
    return this.comments
  }

  get [`get/${CommentActions.GET_DEMO_VIDEO_COMMENTS}`]() {
    return this.demoVideoComments
  }

  get [`get/${CommentActions.GET_COMMENT}`]() {
    return this.comment
  }

  get [`get/${CommentActions.GET_COMMENTS_BY_SOURCE}`]() {
    return this.commentsBySource
  }

  get [`get/${CommentActions.GET_COMMENT_COUNT}`]() {
    return this.commentCount
  }

  get [`get/${CommentActions.NEW_COMMENTS_COUNT}`]() {
    return this.newCommentsCount
  }

  @Mutation
  setCommentCount(count: number) {
    this.commentCount = count
  }

  @Mutation
  setComments(serverResponse: ServerResponse<Comment>) {
    const content = parsingServerResponseContent({
      originalContent: this.comments.content,
      serverResponseContent: serverResponse.content,
      numberOfPage: serverResponse.pageable.pageNumber,
    })
    this.comments = { ...serverResponse, content }
  }

  @Mutation
  setDemoVideoComments(serverResponse: ServerResponse<Comment>) {
    const content = parsingServerResponseContent({
      originalContent: this.demoVideoComments.content,
      serverResponseContent: serverResponse.content,
      numberOfPage: serverResponse.pageable.pageNumber,
    })
    this.demoVideoComments = { ...serverResponse, content }
  }

  @Mutation
  overwriteComments(serverResponse: ServerResponse<Comment>) {
    this.comments = serverResponse
    this.commentCount = serverResponse.totalElements ?? 0
  }

  @Mutation
  overwriteDemoVideoComments(serverResponse: ServerResponse<Comment>) {
    this.demoVideoComments = serverResponse
  }

  @Mutation
  setCommentsBySource(comments: Comment[]) {
    this.commentsBySource = comments
  }

  @Mutation
  setComment(comment: Comment) {
    this.comment = comment
  }

  @Mutation
  removeComment(commentId: string) {
    if (!this.comments?.content) return
    const hasComment = this.comments.content.some(comment => comment.id === commentId)
    if (!hasComment) return

    this.comments.content = this.comments.content.filter(remove => remove.id !== commentId)
    this.comments.totalElements -= 1
    this.commentCount -= 1
  }

  @Mutation
  removeDemoVideoComment(commentId: string) {
    if (!this.demoVideoComments?.content) return
    const hasComment = this.demoVideoComments.content.some(comment => comment.id === commentId)
    if (!hasComment) return

    this.demoVideoComments.content = this.demoVideoComments.content.filter(remove => remove.id !== commentId)
    this.demoVideoComments.totalElements -= 1
  }

  @Mutation
  updateCommentFiles({ id, files }) {
    this.comments.content = this.comments.content.map(comment => {
      if (comment.id === id) {
        comment.fileMetadata = [...comment.fileMetadata ?? [], ...files]
      }
      return comment
    })
  }

  @Mutation
  updateDemoVideoCommentFiles({ id, files }) {
    this.demoVideoComments.content = this.demoVideoComments.content.map(comment => {
      if (comment.id === id) {
        comment.fileMetadata = [...comment.fileMetadata ?? [], ...files]
      }
      return comment
    })
  }

  @Mutation
  updateCommentValueInList({ commentId, field, value } : { commentId: string, field: string, value: any }) {
    this.comments.content = this.comments.content.map(comment => {
      if (comment.id === commentId) {
        comment[field] = value
      }
      return comment
    })
  }

  @Mutation
  updateDemoVideoCommentValueInList({ commentId, field, value } : { commentId: string, field: string, value: unknown }) {
    this.demoVideoComments.content = this.demoVideoComments.content.map(comment => {
      if (comment.id === commentId) {
        comment[field] = value
      }
      return comment
    })
  }

  @Mutation
  onUpdateCommentTextInList({ structuredDescription, commentId } : { structuredDescription: StructuredDescription, commentId: string }) {
    this.comments.content = this.comments.content.map(comment => {
      if (comment.id === commentId) {
        comment.structuredDescription = structuredDescription
      }
      return comment
    })
  }

  @Mutation
  setCommentInList(comment: Comment) {
    const findIndex = this.comments.content.findIndex(c => c.id === comment.id)
    if (findIndex !== -1) {
      this.comments.content.splice(findIndex, 1, comment)
    } else {
      this.comments.content.unshift(comment)
      this.comments.totalElements += 1
      if (comment.source?.sourceType !== "AUDIT") {
        this.commentCount += 1
      }
    }
  }

  @Mutation
  setDemoVideoCommentInList(comment: Comment) {
    const findIndex = this.demoVideoComments.content.findIndex(c => c.id === comment.id)
    if (findIndex !== -1) {
      this.demoVideoComments.content.splice(findIndex, 1, comment)
    } else {
      this.demoVideoComments.content.unshift(comment)
      this.demoVideoComments.totalElements += 1
    }
  }

  @Mutation
  setCommentRestore(id: string) {
    const index = this.comments.content.findIndex(comment => comment.id === id)
    if (index === -1) return
    const commentToUpdate = this.comments.content[index]

    if (commentToUpdate.itemAuditRecord?.taskBefore) {
      Object.assign(commentToUpdate.itemAuditRecord.taskBefore, { isTaskRestored: false })
    }
    const updatedComment = Object.assign(new Comment(), commentToUpdate)

    this.comments.content.splice(index, 1, updatedComment)
  }

  @Mutation
  setNewCommentsCount(count: number) {
    this.newCommentsCount = count
  }

  @Mutation
  replaceCommentInList({ commentId, comment } : { commentId: string, comment: Comment }) {
    this.comments.content = this.comments.content.map(c => {
      if (c.id === commentId) {
        return comment
      }
      return c
    })
  }

  @Mutation
  replaceDemoVideoCommentInList({ commentId, comment } : { commentId: string, comment: Comment }) {
    this.demoVideoComments.content = this.demoVideoComments.content.map(c => {
      if (c.id === commentId) {
        return comment
      }
      return c
    })
  }

  @Action
  async [CommentActions.GET_COMMENTS]({ params, projectScoped = true, onlyGet = false } : {
    params: FilterComment,
    projectScoped?: boolean,
    /** Lets the parent own the response instead of writing it to the shared bucket. */
    onlyGet?: boolean
  }) {
    const selectedProjectId = projectScoped ? params.projectId || this.projectId : undefined
    const filter = FilterComment.create({ ...params, projectId: selectedProjectId }).getJsonObj()
    const data = await apiService.get<ServerResponse<Comment>>(Endpoint.GET_COMMENTS(this.accountId), filter)
    return this.handleResponse<ServerResponse<Comment>>(data, (data) => {
      const content: Comment[] = data.content.map(c => Comment.create(c))
      data = { ...data, content }
      if (!onlyGet) {
        this.setComments(data)
      }
      return data
    })
  }

  /**
   * Loads demo video comments into a separate response bucket so the modal does not overwrite the main item comments tab.
   */
  @Action
  async [CommentActions.GET_DEMO_VIDEO_COMMENTS]({ params } : { params: FilterComment}) {
    const selectedProjectId = params.projectId ? params.projectId : this.projectId
    const filter = FilterComment.create({ ...params, projectId: selectedProjectId }).getJsonObj()
    const data = await apiService.get<ServerResponse<Comment>>(Endpoint.GET_COMMENTS(this.accountId), filter)
    return this.handleResponse<ServerResponse<Comment>>(data, (data) => {
      const content: Comment[] = data.content.map(c => Comment.create(c))
      data = { ...data, content }
      this.setDemoVideoComments( data )
    })
  }

  @Action
  async [CommentActions.GET_COMMENT](commentId: string) {
    const data = await apiService.get<Comment>(Endpoint.GET_COMMENT(this.accountId, commentId), {
      projectId: this.projectId
    })
    return this.handleResponse<Comment>(data, (data) => {
      this.setComment(Comment.create(data))
    })
  }

  @Action
  async [CommentActions.GET_COMMENTS_BY_SOURCE]({ sourceId, sourceType }:{ sourceId: string, sourceType: string }) {
    const data = await apiService.get<Comment[]>(Endpoint.GET_COMMENTS_BY_SOURCE(this.accountId, sourceId, sourceType), {
      projectId: this.projectId
    })
    return this.handleResponse<Comment[]>(data, (data) => {
      const content: Comment[] = data.map(c => Comment.create(c))
      this.setCommentsBySource(content)
    })
  }

  @Action
  async [CommentActions.POST_COMMENT]({ comment, hideToastMessage, projectId, projectScoped = true, skipAddToList } : { comment: Comment, hideToastMessage?: boolean, projectId?: string, projectScoped?: boolean, skipAddToList?: boolean }) {
    const selectedProjectId = projectScoped ? projectId || this.projectId : undefined
    comment.feContext = comment.feContext?.changeId ? comment.feContext : { changeId: uuidv4() }
    const data = await apiService.post<Comment>(Endpoint.POST_COMMENT(this.accountId), comment.getJsonObj(), {
      params: selectedProjectId ? { projectId: selectedProjectId } : undefined
    })
    return this.handleResponse<Comment>(data, (data) => {
      const updateComment = Comment.create(data)
      this.setComment(updateComment)
      if (!skipAddToList) {
        this.setCommentInList(updateComment)
      }
      if (!hideToastMessage) {
        const alertContent: AlertContentItem[] = [{
          text: `Comment ${$t("alert.created")}`,
          type: "regular",
        }]
        alertModule[AlertActions.SHOW_ALERT]({
          content: alertContent,
          type: AlertType.SUCCESS,
          theme: "toast",
        })
      }

      return updateComment
    })
  }

  @Action
  async [CommentActions.UPDATE_COMMENT_DESCRIPTION]({
    comment,
    projectId,
    projectScoped = true,
    skipUpdateList = false
  } : {
    comment: Comment,
    projectId?: string,
    projectScoped?: boolean,
    /** Used by component-owned comment lists. */
    skipUpdateList?: boolean
  }) {
    const selectedProjectId = projectScoped ? projectId || this.projectId : undefined
    const isStatusChangeComment = comment.type === CommentContentType.STATUS
    const endpoint = isStatusChangeComment
      ? Endpoint.UPDATE_STATUS_DESCRIPTION(this.accountId, comment.id)
      : Endpoint.UPDATE_COMMENT_DESCRIPTION(this.accountId, comment.id)
    const payload = isStatusChangeComment
      ? {
          structuredDescription: comment.structuredDescription,
          feContext: comment.feContext
        }
      : comment.getJsonObj()
    const requestConfig = isStatusChangeComment || !selectedProjectId
      ? undefined
      : { params: { projectId: selectedProjectId } }
    const data = await apiService.patch<Comment>(endpoint, payload, requestConfig)
    return this.handleResponse<Comment>(data, (data) => {
      const updateComment = Comment.create(data)
      this.setComment(updateComment)
      if (!skipUpdateList) {
        this.setCommentInList(updateComment)
      }
      this.replaceDemoVideoCommentInList({ commentId: updateComment.id, comment: updateComment })
      const alertContent: AlertContentItem[] = [{
        text: `Comment ${$t("alert.updated")}`,
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
      return updateComment
    })
  }

  @Action
  async [CommentActions.DELETE_COMMENT]({ comment } : { comment: Comment, projectId?: string }) {
    const data = await apiService.patch<Comment>(Endpoint.COMMENT_MARK_AS_DELETED(this.accountId, comment.id))
    return this.handleResponse<Comment>(data, () => {
      this.removeComment(comment.id)
      this.removeDemoVideoComment(comment.id)
      const alertContent: AlertContentItem[] = [{
        text: `Comment ${$t("alert.deleted")}`,
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
    })
  }

  @Action
  async [CommentActions.GET_COMMENT_COUNT](payload: PayloadCommentCount) {
    const { targetId, onlyGet } = payload
    const selectedProjectId = payload.projectScoped === false
      ? undefined
      : payload.projectId || this.projectId
    const data = await apiService.get<number>(Endpoint.GET_COMMENT_COUNT(this.accountId), {
      targetId,
      ...(selectedProjectId ? { projectId: selectedProjectId } : {})
    })
    return this.handleResponse<number>(data, (data) => {
      if (!onlyGet) {
        this.setCommentCount(data)
      }
      return data
    }, undefined, undefined, true)
  }

  @Action
  async [CommentActions.SET_COMMENT_REACTION]({ commentId, reactionType, skipUpdateList = false } : {
    commentId: string,
    reactionType: EmojiType,
    skipUpdateList?: boolean
  }) {
    const data = await apiService.patch<Comment>(Endpoint.COMMENT_REACTION(this.accountId, commentId), null, {
      params: { reactionType }
    })
    return this.handleResponse<Comment>(data, (data) => {
      const updatedComment = Comment.create(data)
      if (!skipUpdateList) {
        this.setCommentInList(updatedComment)
        this.replaceDemoVideoCommentInList({ commentId: updatedComment.id, comment: updatedComment })
      }
      return updatedComment
    })
  }

  @Action
  async [CommentActions.UPDATE_COMMENT_TEXT_IN_LIST]({ structuredDescription, commentId } : { structuredDescription: StructuredDescription, commentId: string }) {
    this.onUpdateCommentTextInList({ structuredDescription, commentId })
  }

  @Action
  [CommentActions.SET_FILES_TO_COMMENT]({ id, files }) {
    this.updateCommentFiles({ id, files })
  }

  @Action
  [CommentActions.SET_FILES_TO_DEMO_VIDEO_COMMENT]({ id, files }) {
    this.updateDemoVideoCommentFiles({ id, files })
  }

  @Action
  [CommentActions.UPDATE_COMMENT_RESTORE]( { id } ) {
    this.setCommentRestore( id )
  }

  @Action
  [CommentActions.UPDATE_COMMENT_VALUE_IN_LIST]({ commentId, field, value } : { commentId: string, field: string, value: any }) {
    this.updateCommentValueInList({ commentId, field, value })
  }

  @Action
  [CommentActions.UPDATE_DEMO_VIDEO_COMMENT_VALUE_IN_LIST]({ commentId, field, value } : { commentId: string, field: string, value: unknown }) {
    this.updateDemoVideoCommentValueInList({ commentId, field, value })
  }

  @Action
  [CommentActions.RESET_COMMENTS]() {
    this.setComments(new ServerResponse<Comment>())
    this.setCommentCount(0)
    this.setNewCommentsCount(0)
  }

  /**
   * Clears only the demo video comments bucket before opening the modal for a new item.
   */
  @Action
  [CommentActions.RESET_DEMO_VIDEO_COMMENTS]() {
    this.setDemoVideoComments(new ServerResponse<Comment>())
  }

  @Action
  [CommentActions.NEW_COMMENTS_COUNT](count: number) {
    this.setNewCommentsCount(count)
  }

  @Action
  [CommentActions.SET_COMMENT_IN_LIST](comment: Comment ) {
    this.setCommentInList(comment)
  }

  @Action
  [CommentActions.SET_DEMO_VIDEO_COMMENT_IN_LIST](comment: Comment ) {
    this.setDemoVideoCommentInList(comment)
  }

  @Action
  [CommentActions.REMOVE_COMMENT_FROM_LIST](commentId: string) {
    this.removeComment(commentId)
  }

  @Action
  [CommentActions.REMOVE_DEMO_VIDEO_COMMENT_FROM_LIST](commentId: string) {
    this.removeDemoVideoComment(commentId)
  }

  @Action
  [CommentActions.SET_COMMENT_RESPONSE](serverResponse: ServerResponse<Comment>) {
    this.overwriteComments(serverResponse)
  }

  @Action
  [CommentActions.SET_DEMO_VIDEO_COMMENT_RESPONSE](serverResponse: ServerResponse<Comment>) {
    this.overwriteDemoVideoComments(serverResponse)
  }

  @Action
  [CommentActions.REPLACE_COMMENT_IN_LIST]({ commentId, comment } : { commentId: string, comment: Comment }) {
    this.replaceCommentInList({ commentId, comment })
  }

  @Action
  [CommentActions.REPLACE_DEMO_VIDEO_COMMENT_IN_LIST]({ commentId, comment } : { commentId: string, comment: Comment }) {
    this.replaceDemoVideoCommentInList({ commentId, comment })
  }

  @Action
  [CommentActions.SUBSCRIPTION_COMMENTS_CHANGE]({ value, includeAudit } : { value: SubscriptionCommentDTO[], includeAudit: boolean }) {
    const content = includeAudit
      ? value
      : value.filter(e => {
          const comment = e.comment
          const hasCommentAuditRecord = !!comment?.commentAuditRecord
          const hasEpicAuditRecord = !!comment?.epicAuditRecord
          const hasIdeaAuditRecord = !!comment?.ideaAuditRecord &&
            comment.source?.sourceType !== "IDEA_STATUS_CHANGE"
          const hasHiddenItemAuditRecord = !!comment?.itemAuditRecord &&
            comment.source?.sourceType !== "ITEM_STATUS_CHANGE"

          return !hasCommentAuditRecord && !hasEpicAuditRecord && !hasIdeaAuditRecord && !hasHiddenItemAuditRecord
        })
    content.forEach((subscription) => {
      const { comment, changeType } = subscription

      const incomingChangeId = comment?.feContext?.changeId
      const existsInListByChangeId = !!incomingChangeId &&
        this.comments.content.some((c) => c.feContext?.changeId === incomingChangeId)
      const isCurrentCommentByChangeId = !!incomingChangeId &&
        this.comment?.feContext?.changeId === incomingChangeId

      // A comment keeps its changeId on edit or deletion. Only creation echoes need filtering.
      const existsInListById = this.comments.content.some(c => c.id === comment.id)
      if (changeType === "CREATED" && (existsInListById || existsInListByChangeId || isCurrentCommentByChangeId)) return

      switch (changeType) {
        case 'CREATED': {
          const newComment = Comment.create(comment)
          this.setCommentInList(newComment)
          this.setNewCommentsCount(this.newCommentsCount + 1)
          break
        }
        case 'UPDATED': {
          const updatedComment = Comment.create(comment)
          if (this.comments.content.find((c) => c.id === updatedComment.id)) {
            this.setCommentInList(updatedComment)
          }
          break
        }
        case 'DELETED':
          this.removeComment(comment.id)
          break
      }
    })
  }
}

export const commentModule = new CommentModule({
  store,
  name: Modules.COMMENT,
})
